import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { usePermission } from '../contexts/PermissionContext';
import { SECTION_ROUTE_MAP } from '../config/sectionRoutes';
import { Compass, Award, Gift, GraduationCap, Target, TrendingUp, GitBranch, AlertTriangle, XOctagon } from 'lucide-react';
import './MainPage.css';
import logoMapGroup from '../logo-map-group.png';

function MainPage() {
    const navigate = useNavigate();
    const { user, logout } = useAuth();
    const { accessibleSections, isLoading: permissionLoading } = usePermission();
    const [hoveredCard, setHoveredCard] = useState(null);

    // 관리자 역할 확인
    const isAdmin = user?.role === 'super_admin' || user?.role === 'admin';
    // 체크박스 상태 관리 (초기값: 모두 체크됨)
    const [checkedItems, setCheckedItems] = useState({});

    // 전체 메뉴 아이템 정의
    const allMenuItems = [
        {
            id: 1,
            title: '비전',
            subtitle: 'Vision',
            description: '목표와 미래를 함께 만들어갑니다',
            icon: Compass,
            iconColor: '#6366F1',
        },
        {
            id: 2,
            title: '인정',
            subtitle: 'Recognition',
            description: '당신의 노력과 성과를 인정합니다',
            icon: Award,
            iconColor: '#8B5CF6',
        },
        {
            id: 3,
            title: '보상',
            subtitle: 'Reward',
            description: '공정한 평가와 보상 시스템',
            icon: Gift,
            iconColor: '#F59E0B',
        },
        {
            id: 4,
            title: '교육',
            subtitle: 'Education',
            description: '지속적인 성장을 위한 학습 기회',
            icon: GraduationCap,
            iconColor: '#10B981',
        },
    ];

    // 권한에 따라 필터링된 메뉴 아이템
    const menuItems = useMemo(() => {
        // 관리자는 모든 섹션에 접근 가능
        if (isAdmin) {
            return allMenuItems;
        }
        // 권한이 있는 섹션만 필터링
        return allMenuItems.filter(item =>
            accessibleSections.some(section => section.name === item.title)
        );
    }, [accessibleSections, isAdmin]);

    useEffect(() => {
        const isLoggedIn = localStorage.getItem('isLoggedIn');
        if (!isLoggedIn) {
            navigate('/login');
        }
    }, [navigate]);

    // 체크박스 상태 초기화 (항상 4개 기준)
    useEffect(() => {
        const initialChecked = {};
        allMenuItems.forEach(item => {
            initialChecked[item.id] = true;
        });
        setCheckedItems(initialChecked);
    }, []);

    const handleLogout = async () => {
        await logout();
        navigate('/login');
    };

    const handleCardClick = (itemId) => {
        if (itemId === 1) {
            navigate('/vision');
        } else if (itemId === 2) {
            navigate('/recognition');
        } else if (itemId === 3) {
            navigate('/reward');
        } else if (itemId === 4) {
            navigate('/education');
        }
    };

    // 체크박스 토글 핸들러
    const handleCheckboxChange = (itemId) => {
        setCheckedItems((prev) => ({
            ...prev,
            [itemId]: !prev[itemId],
        }));
    };

    // 체크된 항목 개수 계산 (항상 4개 기준)
    const checkedCount = Object.values(checkedItems).filter(Boolean).length;
    const totalCount = allMenuItems.length;
    const lackCount = totalCount - checkedCount; // 결여 개수 (전체 - 체크된 개수)

    // 현재 상태 가져오기
    const getCurrentStatus = () => {
        // 결여 비율에 따른 상태 결정
        const lackRatio = totalCount > 0 ? lackCount / totalCount : 0;

        if (lackRatio >= 1) {
            return {
                level: `${lackCount}개 결여`,
                stage: 'OFF STAGE',
                description: '이탈 완료 상태, 마음·관심이 이미 조직 밖에 나가 있는 단계',
                color: '#FF3B30',
                bgColor: 'rgba(255, 59, 48, 0.08)',
                Icon: XOctagon,
            };
        } else if (lackRatio >= 0.75) {
            return {
                level: `${lackCount}개 결여`,
                stage: 'EXIT MODE',
                description: '이탈 직전, 이직 시나리오를 구체적으로 그리고 있는 단계',
                color: '#FF6B35',
                bgColor: 'rgba(255, 107, 53, 0.08)',
                Icon: AlertTriangle,
            };
        } else if (lackRatio >= 0.5) {
            return {
                level: `${lackCount}개 결여`,
                stage: 'CROSSROAD',
                description: '갈림길 구간, 남을지 떠날지 저울질하는 상태',
                color: '#FF9500',
                bgColor: 'rgba(255, 149, 0, 0.08)',
                Icon: GitBranch,
            };
        } else if (lackRatio > 0) {
            return {
                level: `${lackCount}개 결여`,
                stage: 'STEADY STATE',
                description: '유지 구간, 당장은 괜찮지만 한 축의 불안 요소가 있는 상태',
                color: '#34C759',
                bgColor: 'rgba(52, 199, 89, 0.08)',
                Icon: TrendingUp,
            };
        } else {
            return {
                level: '0개 결여',
                stage: 'LONG RUN',
                description: '장기 동행, 모든 축이 충족되어 장기 계획을 세우는 단계',
                color: '#3182F6',
                bgColor: 'rgba(49, 130, 246, 0.08)',
                Icon: Target,
            };
        }
    };

    const currentStatus = getCurrentStatus();

    return (
        <div className="main-page">
            <header className="main-header">
                <div className="header-left">
                    <img src={logoMapGroup} alt="MAP GROUP" className="logo-image" />
                    <p className="logo-subtitle">METARICH AI PATH</p>
                </div>
                <div className="header-right">
                    <div className="user-info">
                        <div className="user-avatar">👤</div>
                        <span className="user-name">{user?.name || '사용자'}</span>
                    </div>
                    {isAdmin && (
                        <button className="admin-btn" onClick={() => navigate('/admin')}>
                            ⚙️ 관리자
                        </button>
                    )}
                    <button className="logout-btn" onClick={handleLogout}>
                        로그아웃
                    </button>
                </div>
            </header>

            <div className="main-content">
                <div className="content-header">
                    <h2 className="content-title">장기적인 커리어를 위해 반드시 점검해야 할 네 가지 축</h2>
                    <div className="content-description-with-checkboxes">
                        {allMenuItems.map((item, index) => (
                            <React.Fragment key={item.id}>
                                {index > 0 && <span className="separator">·</span>}
                                <label className="checkbox-label">
                                    <input
                                        type="checkbox"
                                        checked={checkedItems[item.id] || false}
                                        onChange={() => handleCheckboxChange(item.id)}
                                    />
                                    <span className={checkedItems[item.id] ? 'checked' : ''}>{item.title}</span>
                                </label>
                            </React.Fragment>
                        ))}
                    </div>

                    {/* 현재 상태 표시 */}
                    <div
                        className="current-status-display"
                        style={{
                            backgroundColor: currentStatus.bgColor,
                            borderColor: currentStatus.color
                        }}
                    >
                        <span className="status-level-display" style={{ color: currentStatus.color }}>
                            {currentStatus.level}
                        </span>
                        <span className="status-stage-display">· {currentStatus.stage} ·</span>
                        <span className="status-description-display">{currentStatus.description}</span>
                    </div>
                </div>

                <div className="menu-grid-wrapper">
                    {permissionLoading ? (
                        <div className="loading-container" style={{ textAlign: 'center', padding: '40px' }}>
                        </div>
                    ) : menuItems.length === 0 ? (
                        <div className="no-access-container" style={{ textAlign: 'center', padding: '40px' }}>
                            <p>접근 가능한 섹션이 없습니다.</p>
                            <p style={{ fontSize: '14px', color: '#888' }}>관리자에게 권한을 요청해주세요.</p>
                        </div>
                    ) : (
                    <div className="menu-grid">
                        {menuItems.map((item) => (
                            <div
                                key={item.id}
                                className={`menu-card ${hoveredCard === item.id ? 'hovered' : ''}`}
                                onMouseEnter={() => setHoveredCard(item.id)}
                                onMouseLeave={() => setHoveredCard(null)}
                                onClick={() => handleCardClick(item.id)}
                            >
                                <div className="menu-card-header">
                                    <div className="menu-icon" style={{ backgroundColor: item.iconColor }}>
                                        <item.icon size={22} color="#FFFFFF" strokeWidth={2} />
                                    </div>
                                    <div className="menu-badge" style={{ background: item.iconColor }}></div>
                                </div>

                                <div className="menu-card-body">
                                    <h3 className="menu-title">{item.title}</h3>
                                    <p className="menu-subtitle">{item.subtitle}</p>
                                    <p className="menu-description">{item.description}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default MainPage;
